import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useRegistration } from '../../context/RegistrationContext';
import Logo from '../common/Logo';
import PartyDonationModal from '../common/PartyDonationModal';
import LanguageSwitcher from '../common/LanguageSwitcher';

const ROLE_COLORS = {
  admin: { bg: 'bg-red-100', text: 'text-red-700', ring: 'ring-red-300', avatar: 'bg-red-500' },
  candidate: { bg: 'bg-emerald-100', text: 'text-emerald-700', ring: 'ring-emerald-300', avatar: 'bg-emerald-600' },
  donor: { bg: 'bg-blue-100', text: 'text-blue-700', ring: 'ring-blue-300', avatar: 'bg-blue-500' },
};

const getRoleStyle = (role) =>
  ROLE_COLORS[role?.toLowerCase()] || { bg: 'bg-gray-100', text: 'text-gray-700', ring: 'ring-gray-300', avatar: 'bg-gray-500' };

const ROLE_LABELS = {
  admin: 'Admin',
  candidate: 'Gurukul',
  donor: 'Donor',
};
const getRoleLabel = (role) => ROLE_LABELS[role?.toLowerCase()] || role || '—';

const getInitials = (user) => {
  if (!user) return '?';
  const name = user.fullName || '';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  if (name.length >= 2) return name.slice(0, 2).toUpperCase();
  return '…';
};

const Header = () => {
  const { t } = useTranslation();
  const { isAuthenticated, isAdmin, isCandidate, isDonor, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { registrationStep } = useRegistration();

  const [showPartyDonateModal, setShowPartyDonateModal] = useState(false);
  const [showNavigationConfirmModal, setShowNavigationConfirmModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showHeader, setShowHeader] = useState(true);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const lastScrollY = useRef(0);
  const dropdownRef = useRef(null);

  const shouldShowConfirmModal = location.pathname === '/register' && registrationStep > 1;

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Hide header on scroll down, show on scroll up
  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY || window.pageYOffset;
      if (currentY < 80) { setShowHeader(true); lastScrollY.current = currentY; return; }
      if (currentY > lastScrollY.current) setShowHeader(false);
      else if (currentY < lastScrollY.current) setShowHeader(true);
      lastScrollY.current = currentY;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
    setMobileMenuOpen(false);
    setDropdownOpen(false);
  };

  const handleNavClick = (e, path) => {
    if (shouldShowConfirmModal && path !== location.pathname) {
      e.preventDefault();
      setPendingNavigation(path);
      setShowNavigationConfirmModal(true);
    }
    setMobileMenuOpen(false);
    setDropdownOpen(false);
  };

  const handleConfirmNavigation = () => {
    setShowNavigationConfirmModal(false);
    if (pendingNavigation) { navigate(pendingNavigation); setPendingNavigation(null); }
  };
  const handleCancelNavigation = () => { setShowNavigationConfirmModal(false); setPendingNavigation(null); };
  const closeMobileMenu = () => setMobileMenuOpen(false);

  const roleStyle = getRoleStyle(user?.role);
  const initials = getInitials(user);

  return (
    <header
      className={`sticky top-0 z-50 bg-white shadow-sm border-b border-gray-100 transform transition-transform duration-300 ${showHeader ? 'translate-y-0' : '-translate-y-full'
        }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">

          {/* ── Logo ── */}
          <Link to="/" onClick={(e) => handleNavClick(e, '/')} className="flex items-center hover:opacity-80 transition-opacity">
            <Logo size="md" />
          </Link>

          {/* ── Desktop Navigation ── */}
          <div className="hidden lg:flex items-center gap-2">

            {/* Public links */}
            <Link
              to="/gurukuls"
              onClick={(e) => handleNavClick(e, '/gurukuls')}
              className="text-gray-700 hover:text-primary-600 font-medium px-3 py-2 rounded-lg hover:bg-gray-50 transition"
            >
              {t('nav.gurukul')}
            </Link>
            <Link
              to="/donations"
              onClick={(e) => handleNavClick(e, '/donations')}
              className="text-gray-700 hover:text-primary-600 font-medium px-3 py-2 rounded-lg hover:bg-gray-50 transition"
            >
              {t('nav.donations')}
            </Link>
            <button
              onClick={() => setShowPartyDonateModal(true)}
              className="flex items-center gap-1.5 text-primary-600 hover:text-primary-700 font-semibold px-3 py-2 rounded-lg hover:bg-primary-50 transition"
            >
              <span className="w-6 h-6 flex items-center justify-center bg-primary-100 text-primary-700 rounded-full text-xs font-bold border border-primary-200 shadow-sm">
                रू
              </span>
              {t('nav.donateToGurukulFund')}
            </button>

            <div className="w-px h-6 bg-gray-200 mx-1" />

            {/* Language switcher — always visible */}
            <LanguageSwitcher />

            {isAuthenticated() ? (
              /* ── Avatar Dropdown ── */
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen((prev) => !prev)}
                  className={`flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full border-2 transition-all duration-200 focus:outline-none ${dropdownOpen
                    ? `${roleStyle.ring} border-opacity-100 bg-gray-50`
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  aria-expanded={dropdownOpen}
                  aria-label="User menu"
                >
                  {/* Avatar circle */}
                  <span className={`w-7 h-7 rounded-full ${roleStyle.avatar} text-white text-xs font-bold flex items-center justify-center flex-shrink-0`}>
                    {initials}
                  </span>
                  <span className="text-sm font-semibold text-gray-700 max-w-[120px] truncate">
                    {user?.fullName ? (
                      user.fullName.split(' ')[0]
                    ) : (
                      <svg className="animate-spin w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                    )}
                  </span>
                  {/* Chevron */}
                  <svg
                    className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown panel */}
                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 w-60 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 animate-fadeIn">

                    {/* User identity */}
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {user?.fullName || (
                          <span className="flex items-center gap-1.5 text-gray-400">
                            <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                            </svg>
                            Loading…
                          </span>
                        )}
                      </p>
                      <span className={`inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded-full ${roleStyle.bg} ${roleStyle.text}`}>
                        {getRoleLabel(user?.role)}
                      </span>
                    </div>

                    {/* Nav links */}
                    <div className="py-1">
                      {isAdmin() && (
                        <>
                          <DropdownLink to="/admin" onClick={(e) => handleNavClick(e, '/admin')} icon={
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                          }>
                            {t('nav.admin')}
                          </DropdownLink>
                          <DropdownLink to="/admin/vouchers" onClick={(e) => handleNavClick(e, '/admin/vouchers')} icon={
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 1.343-3 3v7a3 3 0 106 0v-7c0-1.657-1.343-3-3-3z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5h8m-4-2v2" />
                            </svg>
                          }>
                            {t('admin.vouchers.title')}
                          </DropdownLink>
                          <DropdownLink to="/admin/feedback" onClick={(e) => handleNavClick(e, '/admin/feedback')} icon={
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5h6m-6 4h6m-7 4h8m-9 8h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                          }>
                            {t('nav.feedbackAdmin')}
                          </DropdownLink>
                        </>
                      )}
                      <DropdownLink to="/profile" onClick={(e) => handleNavClick(e, '/profile')} icon={
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      }>
                        {t('nav.profile')}
                      </DropdownLink>
                      {(isDonor() || !isCandidate()) && (
                        <DropdownLink to="/my-donations" onClick={(e) => handleNavClick(e, '/my-donations')} icon={
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                          </svg>
                        }>
                          {t('nav.myDonations')}
                        </DropdownLink>
                      )}
                      <DropdownLink to="/my-feedback" onClick={(e) => handleNavClick(e, '/my-feedback')} icon={
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h6m-6 8l-4-4V4a2 2 0 012-2h14a2 2 0 012 2v12a2 2 0 01-2 2H7z" />
                        </svg>
                      }>
                        {t('nav.myFeedback')}
                      </DropdownLink>
                      {isCandidate() && (
                        <DropdownLink to="/gurukul/my-profile" onClick={(e) => handleNavClick(e, '/gurukul/my-profile')} icon={
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                          </svg>
                        }>
                          {t('nav.myGurukulProfile')}
                        </DropdownLink>
                      )}
                      <DropdownLink to="/reports" onClick={(e) => handleNavClick(e, '/reports')} icon={
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      }>
                        {t('nav.reports')}
                      </DropdownLink>
                    </div>

                    {/* Logout */}
                    <div className="border-t border-gray-100 pt-1 mt-1">
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition rounded-lg mx-0"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        {t('nav.logout')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link
                  to="/register-gurukul"
                  onClick={(e) => handleNavClick(e, '/register-gurukul')}
                  className="px-5 py-2 rounded-button border-2 border-secondary-500 text-secondary-600 hover:bg-secondary-50 transition font-medium"
                >
                  {t('nav.registerGurukul')}
                </Link>
                <Link
                  to="/login"
                  onClick={(e) => handleNavClick(e, '/login')}
                  className="bg-secondary-500 text-white px-5 py-2 rounded-button hover:bg-secondary-600 transition font-medium"
                >
                  {t('nav.login')}
                </Link>
              </>
            )}
          </div>

          {/* ── Mobile Menu Button ── */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {/* ── Mobile Menu ── */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-100 animate-fadeIn">
            <div className="flex flex-col space-y-1">
              {/* User identity banner */}
              {isAuthenticated() && user && (
                <div className={`flex items-center gap-3 px-3 py-3 rounded-xl mb-2 ${roleStyle.bg}`}>
                  <span className={`w-9 h-9 rounded-full ${roleStyle.avatar} text-white text-sm font-bold flex items-center justify-center flex-shrink-0`}>
                    {initials}
                  </span>
                  <div>
                    <p className={`text-sm font-semibold ${roleStyle.text} truncate`}>{user.fullName || '—'}</p>
                    <p className={`text-xs ${roleStyle.text} opacity-70`}>{getRoleLabel(user.role)}</p>
                  </div>
                </div>
              )}

              <Link to="/gurukuls" onClick={(e) => handleNavClick(e, '/gurukuls')}
                className="text-primary-700 hover:text-primary-900 font-medium py-2 px-3 rounded-lg hover:bg-primary-50 transition">
                {t('nav.gurukul')}
              </Link>
              <Link to="/donations" onClick={(e) => handleNavClick(e, '/donations')}
                className="text-primary-700 hover:text-primary-900 font-medium py-2 px-3 rounded-lg hover:bg-primary-50 transition">
                {t('nav.donations')}
              </Link>
              <button
                onClick={() => { setShowPartyDonateModal(true); closeMobileMenu(); }}
                className="text-primary-600 hover:text-primary-700 font-semibold flex items-center py-2 px-3 rounded-lg hover:bg-primary-50 transition text-left"
              >
                <span className="w-5 h-5 mr-2 flex items-center justify-center bg-primary-100 text-primary-700 rounded-full text-base font-bold border border-primary-200">
                  रू
                </span>
                {t('nav.donateToGurukulFund')}
              </button>

              {isAuthenticated() ? (
                <>
                  <div className="border-t border-gray-100 my-1" />
                  {isAdmin() && (
                    <>
                      <Link
                        to="/admin"
                        onClick={(e) => handleNavClick(e, '/admin')}
                        className="text-primary-700 hover:text-primary-900 font-medium py-2 px-3 rounded-lg hover:bg-primary-50 transition"
                      >
                        {t('nav.admin')}
                      </Link>
                      <Link
                        to="/admin/vouchers"
                        onClick={(e) => handleNavClick(e, '/admin/vouchers')}
                        className="text-green-700 hover:text-green-900 font-medium py-2 px-3 rounded-lg hover:bg-green-50 transition"
                      >
                        {t('admin.vouchers.title')}
                      </Link>
                      <Link
                        to="/admin/feedback"
                        onClick={(e) => handleNavClick(e, '/admin/feedback')}
                        className="text-primary-700 hover:text-primary-900 font-medium py-2 px-3 rounded-lg hover:bg-primary-50 transition"
                      >
                        {t('nav.feedbackAdmin')}
                      </Link>
                    </>
                  )}
                  <Link to="/profile" onClick={(e) => handleNavClick(e, '/profile')}
                    className="text-primary-700 hover:text-primary-900 font-medium py-2 px-3 rounded-lg hover:bg-primary-50 transition">
                    {t('nav.profile')}
                  </Link>
                  {!isCandidate() && (
                    <Link to="/my-donations" onClick={(e) => handleNavClick(e, '/my-donations')}
                      className="text-primary-700 hover:text-primary-900 font-medium py-2 px-3 rounded-lg hover:bg-primary-50 transition">
                      {t('nav.myDonations')}
                    </Link>
                  )}
                  <Link to="/my-feedback" onClick={(e) => handleNavClick(e, '/my-feedback')}
                    className="text-primary-700 hover:text-primary-900 font-medium py-2 px-3 rounded-lg hover:bg-primary-50 transition">
                    {t('nav.myFeedback')}
                  </Link>
                  {isCandidate() && (
                    <Link to="/gurukul/my-profile" onClick={(e) => handleNavClick(e, '/gurukul/my-profile')}
                      className="text-primary-700 hover:text-primary-900 font-medium py-2 px-3 rounded-lg hover:bg-primary-50 transition">
                      {t('nav.myGurukulProfile')}
                    </Link>
                  )}
                  <Link to="/reports" onClick={(e) => handleNavClick(e, '/reports')}
                    className="text-primary-700 hover:text-primary-900 font-medium py-2 px-3 rounded-lg hover:bg-primary-50 transition">
                    {t('nav.reports')}
                  </Link>
                  <div className="py-2 px-3">
                    <LanguageSwitcher />
                  </div>
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 text-red-600 font-medium py-2 px-3 rounded-lg hover:bg-red-50 transition text-left"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    {t('nav.logout')}
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={(e) => handleNavClick(e, '/login')}
                    className="text-primary-700 hover:text-primary-900 font-medium py-2 px-3 rounded-lg hover:bg-primary-50 transition">
                    {t('nav.login')}
                  </Link>
                  <Link
                    to="/register-gurukul"
                    onClick={(e) => handleNavClick(e, '/register-gurukul')}
                    className="text-primary-700 hover:text-primary-900 font-medium py-2 px-3 rounded-lg hover:bg-primary-50 transition"
                  >
                    {t('nav.registerGurukul')}
                  </Link>
                  <div className="py-2 px-3">
                    <LanguageSwitcher />
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* ── Party Donation Modal ── */}
      <PartyDonationModal
        isOpen={showPartyDonateModal}
        onClose={() => setShowPartyDonateModal(false)}
      />

      {/* ── Navigation Confirmation Modal ── */}
      {showNavigationConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 animate-fadeIn">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900">{t('modals.leavePageTitle')}</h3>
            </div>
            <p className="text-gray-600 mb-6">{t('modals.leavePageMessage')}</p>
            <div className="flex gap-3">
              <button
                onClick={handleCancelNavigation}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-button font-semibold hover:bg-gray-200 transition"
              >
                {t('actions.stayHere')}
              </button>
              <button
                onClick={handleConfirmNavigation}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-button font-semibold hover:bg-red-700 transition"
              >
                {t('actions.leavePage')}
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

/* ── Reusable dropdown link item ── */
const DropdownLink = ({ to, onClick, icon, children }) => (
  <Link
    to={to}
    onClick={onClick}
    className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary-600 transition"
  >
    <span className="text-gray-400">{icon}</span>
    {children}
  </Link>
);

export default Header;
